import React, { useEffect, useRef, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Container,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import mapImage from "../../../assets/images/map-area.png";
import editIcon from "../../../assets/images/editIcon.png";
import QuotationDialog from "./QuotationDialog";
import PhotosMap from "./PhotosMap";
import userIcon from "../../../assets/images/userIcon.png";
import { useApp } from "../../../Context/AppContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createProject } from "../../../apis/apisList/userApi";
import planConfig from "../../../config/planConfig";
import moment from "moment";
import { useLeavePageConfirm } from "../../../usePrompt";
import { AddCircle } from "@mui/icons-material";

const ProjectDetail = ({ isPreview, pName }) => {
  const {
    user,
    previewData,
    projectData,
    setProjectData,
    setSelectedOption,
    setShouldWarn,
    shouldWarn,
    setCurrentStep,
    setPrName,
  } = useApp();
  const { projectName: title } = previewData;

  const [data, setData] = useState([]);
  const [quoteDialog, setQuoteDialog] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [address, setAddress] = useState(title);
  const [dataLength, setDataLength] = useState(0);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const projectRef = useRef(null);

  const handleAddMore = (option = "Photos Plan", id) => {
    setPrName(projectName);
    if (option === "new") {
      setCurrentStep(2);
      localStorage.setItem("edit_project", true);
      localStorage.setItem("project_add_more", true);
      setSelectedOption(null);
      navigate("/");
    } else {
      // setProjectData(data);
      localStorage.setItem("edit_project", true);
      localStorage.setItem("edit_project_id", id);
      setSelectedOption(option);
      navigate("/");
    }
  };

  useEffect(() => {
    if (pName) {
      setProjectName(pName);
    }
  }, [pName]);

  const handlePlanSave = async () => {
    try {
      setLoading(true);
      const projectData = localStorage.getItem("projectData");
      const user = JSON.parse(localStorage.getItem("user")) ?? null;
      if (!projectData) {
        toast.error("No project data found. Please select a plan first.");
        return;
      }
      if (!user) {
        toast.error("Please login first to save plans");
        return;
      }

      const { data, address: pAddress } = JSON.parse(projectData);

      const price = data.reduce((acc, curr) => acc + curr.result.price, 0);

      const quoteData = {
        name: projectName || "untitled",
        contact: {
          fullName: user.fullName,
          email: user.email,
          phone: user.phone || "",
        },
        plans: JSON.stringify(data),
        price,
        address: address || pAddress,
        isRegistered: true,
        isDraft: true,
        user: user._id,
      };

      await createProject(quoteData);
      setLoading(false);
      toast.success("Quote Saved successfully!");
      localStorage.removeItem("edit_project");
      localStorage.removeItem("edit_id");
      localStorage.removeItem("project_add_more");
      localStorage.removeItem("edit_project_id");
      localStorage.removeItem("projectData");
      setProjectData([]);
      navigate("/dashboard");
    } catch (error) {
      setLoading(false);
      toast.error(
        "Oops. Something went wrong. Please screenshot this screen and email (please note property address) to flights@flyover.io and we’ll send you a quote, or call us at (608) 571 7992",
        {
          autoClose: false,
        }
      );
      console.error("Error during Save quote", error);
    }
  };

  const handleRequestQuote = () => {
    if (!projectName.trim()) {
      projectRef.current?.focus();
      toast.error("Please enter project name");
    } else {
      setQuoteDialog(true);
    }
  };

  useEffect(() => {
    if (!user || pName) return;
    const name = `${user?.username}/${moment().format(
      "MM-DD-YYYY"
    )}/${moment().format("hh:mm A")}`;
    setProjectName(name);
  }, [user, pName]);

  useEffect(() => {
    const localData = localStorage.getItem("projectData");
    if (localData) {
      const parsedData = JSON.parse(localData);
      setAddress(parsedData.address);
      setProjectName(parsedData.projectName);
    }
    if (projectData.length) {
      setData(projectData);
      setDataLength(projectData.length);
    } else {
      setData(previewData.data);
      setProjectData(previewData.data);
      setDataLength(previewData.data.length);
    }
  }, []);

  const redirectUrl = `/dashboard/preview?savePopup=true&project=${projectName}`;

  const { confirmNavigate } = useLeavePageConfirm({
    shouldWarn,
    message: "Are you sure you want to leave this page?",
  });
  return (
    <Container maxWidth="xl" sx={{ padding: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
          width: "100%",
        }}
      >
        {isPreview ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              mb: 4,
              width: "100%",
            }}
          >
            {/* Project Name */}
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Project Name"
                placeholder="Enter Project Name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                variant="standard"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  sx: { fontSize: "0.9rem", color: "text.secondary" },
                }}
                InputProps={{
                  sx: {
                    fontSize: "1.25rem",
                    fontWeight: 600,
                  },
                }}
                inputRef={projectRef}
                error={!projectName.trim()}
                helperText={
                  !projectName.trim() ? "Please enter project name" : ""
                }
              />
            </Box>

            {/* Address */}
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Address"
                value={address}
                placeholder="Enter Address"
                onChange={(e) => setAddress(e.target.value)}
                variant="standard"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  sx: { fontSize: "0.9rem", color: "text.secondary" },
                }}
                InputProps={{
                  sx: {
                    fontSize: "1.25rem",
                    fontWeight: 600,
                  },
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700} mb={2}>
              {projectName}
            </Typography>
          </Box>
        )}
      </Box>

      {data.length > 0 ? (
        <Grid container spacing={4}>
          {/* Left Side - Image Cards */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {data?.map(
                (
                  {
                    result,
                    features,
                    selectedOption,
                    zoomLevel,
                    mainServiceLabel,
                    subServiceLabel,
                    id,
                  },
                  index
                ) => (
                  <Grid item xs={12} sm={6} key={id || index}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: 3,
                        padding: "10px",
                        position: "relative",
                      }}
                    >
                      <PhotosMap
                        features={features}
                        zoomLevel={zoomLevel}
                        type={selectedOption}
                      />

                      <Button
                        sx={{
                          position: "absolute",
                          zIndex: 1,
                          right: "20px",
                          bottom: "20px",
                        }}
                        onClick={() => handleAddMore(selectedOption, id)}
                      >
                        <img src={editIcon} />
                      </Button>
                    </Card>
                    <Box sx={{ textAlign: "center", mt: "5px" }}>
                      <Typography variant="h6">{result.type}</Typography>
                      {(mainServiceLabel || subServiceLabel) && (
                        <Typography variant="body2" color="text.secondary">
                          {mainServiceLabel}
                          {mainServiceLabel && subServiceLabel ? " • " : ""}
                          {subServiceLabel}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )
              )}
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: 4,
                    height: 310,
                    p: 3,
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    bgcolor: "background.paper",
                    "&:hover": {
                      boxShadow: 6,
                      transform: "translateY(-4px)",
                      bgcolor: "grey.100",
                    },
                  }}
                  onClick={() => handleAddMore("new")}
                >
                  <AddCircle sx={{ fontSize: 40, color: "primary.main" }} />
                  <Box
                    sx={{
                      fontWeight: 500,
                      fontSize: "1rem",
                      color: "text.primary",
                    }}
                  >
                    Add More Services
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Right Side - Project Summary */}
          <Grid item xs={12} md={4} gap={0}>
            <Card
              sx={{
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                mb: 2,
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    bgcolor: "#222",
                    color: "white",
                    p: 2,
                    fontWeight: 500,
                    fontSize: "1.25rem",
                  }}
                >
                  Project Summary
                </Typography>

                <Box sx={{ p: 2 }}>
                  {data.map(({ result }, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        py: 1.5,
                        "&:not(:last-child)": {
                          borderBottom: "1px solid #eee",
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={mapImage}
                        alt={result.type}
                        sx={{
                          width: 80,
                          height: 60,
                          borderRadius: 1,
                          border: "1px solid #ddd",
                        }}
                      />
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          {result.type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {result.type === "Photos Plan" ||
                          result?.type ===
                            planConfig?.ground_control
                              ?.ground_control_points_plan ? (
                            <span>Photos: {result.totalImages}</span>
                          ) : result.type === "Videos Plan" ? (
                            <span>Miles: {result.area}</span>
                          ) : (
                            <span>Acres: {result.area}</span>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
            {isPreview && (
              <Card
                sx={{
                  padding: 2,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  position: "relative",
                  top: "-15px",
                  bgcolor: "#F0F4F9",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{
                      mb: 1.5,
                      bgcolor: "#065F9C",
                      borderRadius: 3,
                      textTransform: "none",
                      py: 1.3,
                      "&:hover": {
                        bgcolor: "#005bb9",
                      },
                      maxWidth: "90%",
                    }}
                    onClick={handleRequestQuote}
                  >
                    Click to Request a Quote
                  </Button>
                  {user && (
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{
                        mb: 1.5,

                        borderRadius: 3,
                        textTransform: "none",
                        py: 1.3,
                        maxWidth: "90%",
                        bgcolor: "green",
                      }}
                      onClick={handlePlanSave}
                      loading={loading}
                      loadingPosition="start"
                    >
                      Save plans
                    </Button>
                  )}
                  {!user && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      width="100%"
                    >
                      <Button
                        fullWidth
                        variant="outlined"
                        sx={{
                          color: "text.primary",
                          bgcolor: "white",
                          borderColor: "black",
                          borderRadius: 3,
                          textTransform: "none",
                          py: 1.3,
                          maxWidth: "90%",
                        }}
                        onClick={() => {
                          if (!projectName.trim()) {
                            projectRef.current?.focus();
                            toast.error("Please enter project name");
                          } else {
                            navigate(
                              `/login?redirect=${encodeURIComponent(
                                redirectUrl
                              )}`
                            );
                          }
                        }}
                        startIcon={<img src={userIcon} />}
                      >
                        Register now to save flight plan
                      </Button>

                      {/* Divider with "or Login" */}
                      <Box
                        display="flex"
                        alignItems="center"
                        width="90%"
                        my={2}
                      >
                        <Divider sx={{ flex: 1, bgcolor: "black" }} />
                        <Typography
                          sx={{ mx: 2, fontSize: "14px", fontWeight: 500 }}
                        >
                          or
                        </Typography>
                        <Divider sx={{ flex: 1, bgcolor: "black" }} />
                      </Box>

                      {/* Login Button */}
                      <Button
                        fullWidth
                        variant="contained"
                        sx={{
                          bgcolor: "rgb(255, 134, 52)",
                          color: "white",
                          borderRadius: 3,
                          textTransform: "none",
                          py: 1.3,
                          maxWidth: "90%",
                        }}
                        onClick={() => {
                          if (!projectName.trim()) {
                            projectRef.current?.focus();
                            toast.error("Please enter project name");
                          } else {
                            navigate(
                              `/login?redirect=${encodeURIComponent(
                                redirectUrl
                              )}`
                            );
                          }
                        }}
                      >
                        Login
                      </Button>
                    </Box>
                  )}
                </div>

                <Typography
                  variant="caption"
                  display="block"
                  textAlign="center"
                  sx={{ mt: 1.5, color: "text.secondary", fontSize: "0.75rem" }}
                >
                  Plan will be saved as a draft on your account.
                </Typography>
              </Card>
            )}
          </Grid>
        </Grid>
      ) : (
        <Box textAlign="center" mt={4}>
          <Typography variant="h4">Record not found</Typography>
        </Box>
      )}

      <QuotationDialog
        open={quoteDialog}
        onClose={() => setQuoteDialog(false)}
        projectName={projectName}
        address={address}
      />
    </Container>
  );
};

export default ProjectDetail;
